import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FileUpload } from '../../core/components';
import { useDocumentUpload } from 'Core/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectOverheadCraneMaintenanceData } from './store/selectors';
import { showToast } from '../../core/redux/toast/slice';
import { addOverheadCraneMaintenanceRecord } from './store/slice';
import ViewAllImagesModal from '../../core/components/viewallimagesmodal/view-all-images-modal';
import WebcamMultiCaptureModal from '../../core/components/modal/webcam-multi-capture-modal';
import SegmentedImagesModal from '../../core/components/modal/segmentedimagesmodal/segmented-images-modal';

const initData = {
  id: 0,
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  serviceRecords: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const OverheadCraneMaintenanceRecordAddForm = ({ data, overheadCraneId, maintenanceRecordId, onCloseModal }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(initData);
  const [pictureDocuments, setPictureDocuments] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [maintenancePersonnels, setMaintenancePersonnels] = useState([]);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [showImageViewerModal, setShowImageViewerModal] = useState(false);
  const [showManageImagesModal, setShowManageImagesModal] = useState(false);
  const [processedImageSegments, setProcessedImageSegments] = useState([]);

  const overheadCraneMaintenanceData = useSelector(selectOverheadCraneMaintenanceData);

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  useEffect(() => {
    if (overheadCraneMaintenanceData?.safetyChecklist) {
      setFormData(prev => ({
        ...prev,
        safetyChecklist: overheadCraneMaintenanceData.safetyChecklist,
      }));
    }
  }, [overheadCraneMaintenanceData]);

  const getNextWorkOrderNumber = () => {
    const timestamp = Date.now();
    return `WO-${timestamp}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (file, field) => {
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file.name,
      }));
    }
  };

  const handleAddPersonnel = () => {
    setMaintenancePersonnels(prev => [
      ...prev,
      {
        personnelName: '',
        startTime: new Date().toISOString().slice(0, 16),
        endTime: new Date().toISOString().slice(0, 16),
      },
    ]);
  };

  const handleRemovePersonnel = index => {
    setMaintenancePersonnels(prev => prev.filter((_, i) => i !== index));
  };

  const handlePersonnelChange = (index, field, value) => {
    setMaintenancePersonnels(prev =>
      prev.map((personnel, i) => (i === index ? { ...personnel, [field]: value } : personnel))
    );
  };

  const handleSave = async () => {
    try {
      // Validate overheadCraneId before saving
      if (!overheadCraneId || overheadCraneId === 0 || overheadCraneId === '0') {
        console.error('Invalid Overhead Crane ID:', overheadCraneId);
        dispatch(showToast({
          message: 'Invalid Overhead Crane ID. Please close and reopen the form.',
          type: 'danger',
        }));
        return;
      }

      console.log('Saving maintenance record with overheadCraneId:', overheadCraneId);

      const payload = {
        ...formData,
        overheadCraneId: overheadCraneId,
        PersonnelRecords: maintenancePersonnels || [],
        ServiceRecords: uploadedFiles || [],
        SafetyChecklist: formData.safetyChecklist || [],
        workOrderNumber: formData.workOrderNumber || getNextWorkOrderNumber(),
        date: formData.date || new Date().toISOString().split('T')[0],
      };

      console.log('Overhead Crane Maintenance Record Payload:', payload);

      await dispatch(addOverheadCraneMaintenanceRecord(payload));
      dispatch(showToast({ message: 'Maintenance record saved successfully', type: 'success' }));
      onCloseModal();
    } catch (error) {
      dispatch(showToast({ message: 'Error saving maintenance record', type: 'error' }));
    }
  };

  const handleCancel = () => {
    onCloseModal();
  };

  const handleCaptureImage = () => {
    setShowWebcamModal(true);
  };

  const handleManageImages = () => {
    setShowManageImagesModal(true);
  };

  const handleViewAllImages = () => {
    setShowImageViewerModal(true);
  };

  const { uploadDocument } = useDocumentUpload();

  const handleFileUpload = async (file, category) => {
    try {
      const result = await uploadDocument(file, 'MACHINE', category);
      if (result.success) {
        setUploadedFiles(prev => [...prev, { ...result.data, category }]);
        return result.data;
      }
    } catch (error) {
      console.error('File upload error:', error);
    }
  };

  return (
    <div className="maintenance-record-form">
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Work Order Number*</label>
          <input
            type="text"
            className="form-control"
            value={formData.workOrderNumber}
            onChange={e => handleInputChange('workOrderNumber', e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Date*</label>
          <input
            type="date"
            className="form-control"
            value={formData.date}
            onChange={e => handleInputChange('date', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <label className="form-label">Description*</label>
          <textarea
            className="form-control"
            rows="4"
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Enter maintenance description"
            required
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Maintenance Type*</label>
          <select
            className="form-control"
            value={formData.maintenanceType}
            onChange={e => handleInputChange('maintenanceType', e.target.value)}
            required
          >
            <option value="1">Preventive</option>
            <option value="2">Corrective</option>
            <option value="3">Emergency</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Personnel Name*</label>
          <input
            type="text"
            className="form-control"
            value={formData.personnelName}
            onChange={e => handleInputChange('personnelName', e.target.value)}
            required
          />
        </div>
      </div>

      {/* Pictures Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Pictures</h6>
          <button
            className="btn btn-primary"
            onClick={() => setShowWebcamModal(true)}
          >
            <i className="fas fa-camera me-2"></i>Capture Image
          </button>
        </div>
      </div>

      {/* Service Completed Documents Section */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="mb-3">Service Completed Documents</h6>
          <div className="d-flex align-items-center mb-2">
            <select className="form-control me-2" style={{ maxWidth: '200px' }}>
              <option>Select document type</option>
              <option>Service Manual</option>
              <option>Warranty</option>
              <option>Certificate</option>
            </select>
            <FileUpload
              onFileSelect={handleFileUpload}
              accept=".pdf,.doc,.docx"
            />
            <button className="btn btn-primary ms-2">Add</button>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>Document Name</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((file, index) => (
                  <tr key={index}>
                    <td>Service Manual</td>
                    <td>{file.fileName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Maintenance Personnel Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Maintenance Personnel</h6>
            <button className="btn btn-info" onClick={handleAddPersonnel}>
              + Add Personnel
            </button>
          </div>
          {maintenancePersonnels.map((personnel, index) => (
            <div key={index} className="row mb-3 border p-3 rounded">
              <div className="col-md-4">
                <label className="form-label">Personnel Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={personnel.personnelName}
                  onChange={e => handlePersonnelChange(index, 'personnelName', e.target.value)}
                  placeholder="Personnel Name"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Start time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={personnel.startTime}
                  onChange={e => handlePersonnelChange(index, 'startTime', e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">End time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={personnel.endTime}
                  onChange={e => handlePersonnelChange(index, 'endTime', e.target.value)}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-danger"
                  onClick={() => handleRemovePersonnel(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Down Time Section */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">Down Time</label>
          <input
            type="text"
            className="form-control"
            value={formData.downTime}
            onChange={e => handleInputChange('downTime', e.target.value)}
            placeholder="Auto-calculated based on status"
            readOnly
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="row">
        <div className="col-12 d-flex justify-content-end">
          <button className="btn btn-secondary me-2" onClick={handleCancel}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={handleSave}>
            Save Overhead Crane Maintenance Record
          </button>
        </div>
      </div>

      {showWebcamModal && (
        <WebcamMultiCaptureModal
          show={showWebcamModal}
          onHide={() => setShowWebcamModal(false)}
          onSave={handleFileUpload}
          context="overhead-crane"
        />
      )}

      {showImageViewerModal && (
        <ViewAllImagesModal
          show={showImageViewerModal}
          onHide={() => setShowImageViewerModal(false)}
          images={processedImageSegments}
        />
      )}

      {showManageImagesModal && (
        <SegmentedImagesModal
          show={showManageImagesModal}
          onHide={() => setShowManageImagesModal(false)}
          context="overhead-crane"
          record="Maintenance"
          maintenanceId={overheadCraneId}
        />
      )}
    </div>
  );
};

OverheadCraneMaintenanceRecordAddForm.propTypes = {
  data: PropTypes.object,
  overheadCraneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maintenanceRecordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseModal: PropTypes.func,
};

export default OverheadCraneMaintenanceRecordAddForm;

